export type ChainEntityRef = { getId(): { serialize(): number } }

const wrapId = (id: number): ChainEntityRef => ({
  getId: () => ({
    serialize: () => id,
  }),
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const unwrapTypedValue = (field: unknown): unknown => {
  if (!isRecord(field)) {
    return field
  }
  if ('value' in field) {
    return field.value
  }
  return field
}

export function normalizeUserFieldsMap(
  userFields: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!userFields || !isRecord(userFields)) {
    return {}
  }
  const nested = userFields.fields
  if (isRecord(nested)) {
    return nested
  }
  return userFields
}

const rowId = (row: unknown): number | null => {
  if (!isRecord(row)) {
    return null
  }
  const nestedFields = isRecord(row.fields) ? row.fields : null
  const nestedNestedFields = nestedFields && isRecord(nestedFields.fields) ? nestedFields.fields : null
  const raw = unwrapTypedValue(row._id ?? nestedFields?._id ?? nestedNestedFields?._id)
  if (raw == null) {
    return null
  }
  const parsed = typeof raw === 'number' ? raw : Number(raw)
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed
}

const collectIdsFromTableLike = (value: unknown, depth = 0): number[] => {
  if (depth > 4 || value == null) {
    return []
  }
  const unwrapped = unwrapTypedValue(value)
  if (typeof unwrapped === 'number') {
    return unwrapped > 0 ? [unwrapped] : []
  }
  if (typeof unwrapped === 'string') {
    const parsed = Number(unwrapped)
    return Number.isNaN(parsed) || parsed <= 0 ? [] : [parsed]
  }
  if (Array.isArray(unwrapped)) {
    return unwrapped.flatMap((item) => collectIdsFromTableLike(item, depth + 1))
  }
  if (!isRecord(unwrapped)) {
    return []
  }

  const directId = rowId(unwrapped)
  if (directId != null) {
    return [directId]
  }

  const ids: number[] = []
  for (const key of ['rows', 'items', 'values', 'value']) {
    if (key in unwrapped) {
      ids.push(...collectIdsFromTableLike(unwrapped[key], depth + 1))
    }
  }
  return ids
}

const collectIdsFromRows = (rows: unknown): number[] => {
  if (!Array.isArray(rows)) {
    return []
  }
  const ids: number[] = []
  for (const row of rows) {
    const id = rowId(row)
    if (id != null) {
      ids.push(id)
    }
  }
  return ids
}

const collectIdsFromTableValue = (value: unknown): number[] => {
  const unwrapped = unwrapTypedValue(value)
  if (unwrapped == null) {
    return []
  }
  if (isRecord(unwrapped) && Array.isArray(unwrapped.rows)) {
    return collectIdsFromRows(unwrapped.rows)
  }
  if (Array.isArray(unwrapped)) {
    const ids: number[] = []
    for (const item of unwrapped) {
      if (typeof item === 'number') {
        ids.push(item)
        continue
      }
      const id = rowId(item)
      if (id != null) {
        ids.push(id)
      }
    }
    return ids
  }
  return collectIdsFromTableLike(unwrapped)
}

const readFieldValue = (fields: Record<string, unknown>, name: string): unknown => {
  if (!(name in fields)) {
    return undefined
  }
  return unwrapTypedValue(fields[name])
}

export function collectEntityRefsFromFields(
  fields: Record<string, unknown>,
  names: string[],
): ChainEntityRef[] {
  const seen = new Set<number>()
  const refs: ChainEntityRef[] = []

  for (const name of names) {
    const ids = collectIdsFromTableValue(readFieldValue(fields, name))
    for (const id of ids) {
      if (seen.has(id)) {
        continue
      }
      seen.add(id)
      refs.push(wrapId(id))
    }
  }

  return refs
}

export function collectRoleIdsForRestrictions(fields: Record<string, unknown>): number[] {
  return collectEntityRefsFromFields(fields, ['allRoles', 'roles']).map((r) => r.getId().serialize())
}

export function readRawChainField(fields: Record<string, unknown>, name: string): unknown {
  return readFieldValue(fields, name)
}
